import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CommentIcon from "@mui/icons-material/Comment";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DialogTableContent from "../components/DialogTableContent";
import BaseDataTable from "../components/BaseDataTable";
import { useDynamicDialog } from "../components/DynamicDialogProvider";
import { useUpdateItp } from "../../AllEquipments/CommonFeatures/PCSTab/ITP/hooks";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../context/authContext";
import { authStore } from "../../../../context/authContext/authStore";
import {
  create4DTask,
  getPlantData,
  getProjectData,
} from "../../AllEquipments/CommonFeatures/PMSTab2/4d/api";
import { useAmsModuleData } from "../context/AmsModuleDataContext";

export const MAINTENANCE_TABS = [
  { key: "equipments", label: "Equipments" },
  { key: "valves", label: "Valves" },
  { key: "instruments", label: "Instruments" },
];

function StatusChip({ value }) {
  const colorMap = {
    Open: "warning",
    Closed: "success",
    Planned: "info",
    Issued: "primary",
    Pending: "default",
    Available: "success",
    "In Stock": "success",
    Critical: "error",
    High: "error",
    Medium: "warning",
    Low: "info",
    Active: "success",
    Draft: "default",
    Completed: "success",
  };

  return (
    <Chip
      label={value || "-"}
      color={colorMap[value] || "default"}
      size="small"
    />
  );
}

function SimpleDetailsContent({ title, items = [] }) {
  return (
    <Box>
      {title ? (
        <Typography variant="h6" mb={2}>
          {title}
        </Typography>
      ) : null}

      <Grid container spacing={2}>
        {items.map((item) => (
          <Grid item xs={12} md={6} key={item.label}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 1.5,
                height: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
              >
                {item.label}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                {item.value === null ||
                item.value === undefined ||
                item.value === ""
                  ? "-"
                  : String(item.value)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/** Normalize row-level data sheet fields for the Trim List maintenance dialog. */
function resolveRegistryDataSheet(rowData) {
  const raw = rowData?.dataSheet ?? rowData?.dataSheets ?? rowData?.data_sheet;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  if (typeof raw === "string") {
    return { Reference: { "Data sheet": raw } };
  }

  if (typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const nested =
    raw.dataSheet &&
    typeof raw.dataSheet === "object" &&
    !Array.isArray(raw.dataSheet)
      ? raw.dataSheet
      : raw;

  return nested;
}

function DataSheetContent({ dataSheet }) {
  console.log("dataSheet", dataSheet);

  const sections =
    dataSheet && typeof dataSheet === "object" ? Object.entries(dataSheet) : [];

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">No data sheet data available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {sections.map(([sectionTitle, sectionFields]) => (
        <Box key={sectionTitle} sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              borderTop: "1px solid #cfd3e6",
              borderBottom: "1px solid #cfd3e6",
              py: 1,
              px: 1,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {sectionTitle}
          </Typography>

          <Box
            sx={{
              border: "1px solid #d9d9d9",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {(() => {
              const fieldsRecord =
                sectionFields !== null &&
                typeof sectionFields === "object" &&
                !Array.isArray(sectionFields)
                  ? sectionFields
                  : { Value: sectionFields };

              const fieldEntries = Object.entries(fieldsRecord);

              return fieldEntries.map(([fieldLabel, fieldValue], index) => (
                <Box
                  key={fieldLabel}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "280px 1fr",
                    borderBottom:
                      index === fieldEntries.length - 1
                        ? "none"
                        : "1px solid #e5e5e5",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRight: "1px solid #e5e5e5",
                      fontWeight: 600,
                    }}
                  >
                    {fieldLabel}
                  </Box>
                  <Box sx={{ px: 2, py: 1 }}>
                    {fieldValue === null ||
                    fieldValue === undefined ||
                    fieldValue === ""
                      ? "-"
                      : String(fieldValue)}
                  </Box>
                </Box>
              ));
            })()}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function PopupActionButtonCell({
  rowData,
  buttonLabel,
  dialogBuilder,
  buttonProps = {},
  startIcon = null,
}) {
  const { openDialog } = useDynamicDialog();

  const handleOpen = (event) => {
    event.stopPropagation();
    openDialog(dialogBuilder(rowData));
  };

  return (
    <Button
      variant="contained"
      size="small"
      onClick={handleOpen}
      startIcon={startIcon || undefined}
      {...buttonProps}
    >
      {buttonLabel}
    </Button>
  );
}

function PopupActionIconCell({
  rowData,
  tooltip,
  dialogBuilder,
  icon,
  color = "primary",
}) {
  const { openDialog } = useDynamicDialog();

  const handleOpen = (event) => {
    event.stopPropagation();
    openDialog(dialogBuilder(rowData));
  };

  return (
    <Tooltip title={tooltip}>
      <IconButton size="small" color={color} onClick={handleOpen}>
        {icon}
      </IconButton>
    </Tooltip>
  );
}

export function createPopupActionColumn({
  id,
  header,
  buttonLabel = "View",
  dialogBuilder,
  buttonProps = {},
  startIcon = null,
  size = 160,
}) {
  return {
    id,
    header,
    size,
    enableSorting: false,
    Cell: ({ row }) => (
      <PopupActionButtonCell
        rowData={row.original}
        buttonLabel={buttonLabel}
        dialogBuilder={dialogBuilder}
        buttonProps={buttonProps}
        startIcon={startIcon}
      />
    ),
  };
}

export function createStaticButtonColumn({
  id,
  header,
  buttonLabel = "Open",
  buttonProps = {},
  size = 160,
}) {
  return {
    id,
    header,
    size,
    enableSorting: false,
    Cell: () => (
      <Button
        variant="contained"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
        }}
        {...buttonProps}
      >
        {buttonLabel}
      </Button>
    ),
  };
}

export function createTextInputColumn({
  id,
  header,
  accessorKey,
  size = 180,
  placeholder = "",
}) {
  return {
    id,
    header,
    size,
    enableSorting: false,
    Cell: ({ row }) => (
      <TextField
        fullWidth
        size="small"
        defaultValue={row.original[accessorKey] || ""}
        placeholder={placeholder}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          row.original[accessorKey] = event.target.value;
        }}
      />
    ),
  };
}

export function createIconPopupActionColumn({
  id,
  header = "",
  tooltip,
  icon,
  dialogBuilder,
  size = 64,
  color = "primary",
}) {
  return {
    id,
    header,
    size,
    enableSorting: false,
    Cell: ({ row }) => (
      <PopupActionIconCell
        rowData={row.original}
        tooltip={tooltip}
        dialogBuilder={dialogBuilder}
        icon={icon}
        color={color}
      />
    ),
  };
}

/* ------------------------------------------
   SPARE PARTS
------------------------------------------ */

function buildSparePartsColumns() {
  return [
    {
      accessorKey: "vendorPartNo",
      header: "Vendor part #",
      size: 160,
    },
    {
      accessorKey: "partDescription",
      header: "Part description",
      size: 220,
    },
    {
      accessorKey: "cost",
      header: "Cost",
      size: 120,
    },
    {
      accessorKey: "deliverySchedule",
      header: "Delivery schedule",
      size: 180,
    },
    {
      accessorKey: "shelfLife",
      header: "Shelf life",
      size: 120,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      Cell: ({ row }) => <StatusChip value={row.original.status} />,
    },
    {
      accessorKey: "qty",
      header: "Qty",
      size: 80,
    },
    {
      accessorKey: "remarks",
      header: "Remarks",
      size: 220,
    },
  ];
}

function buildSparePartsDialog(rowData) {
  return {
    title: `Spare Parts - ${rowData.tagNo || rowData.tag}`,
    maxWidth: "lg",
    renderContent: () => (
      <DialogTableContent
        columns={buildSparePartsColumns()}
        data={rowData.spareParts || []}
        tableProps={{
          enableTopToolbar: false,
          initialState: {
            pagination: { pageIndex: 0, pageSize: 5 },
          },
        }}
      />
    ),
  };
}

function buildDataSheetDialog(rowData) {
  const resolvedSheet = resolveRegistryDataSheet(rowData);

  return {
    title: `Trim List – Data Sheets`,
    maxWidth: "lg",
    renderContent: () => <DataSheetContent dataSheet={resolvedSheet} />,
  };
}

/* ------------------------------------------
   INSPECTION FINDING DETAILS
------------------------------------------ */

function buildFindingSideDialog(rowData, title) {
  return {
    title: `${title} - Finding #${rowData.no}`,
    maxWidth: "md",
    renderContent: () => (
      <SimpleDetailsContent
        title={title}
        items={[
          { label: "Inspection ID", value: rowData.no },
          {
            label: "Inspection/Test Activity",
            value: rowData.inspectionTestActivity,
          },
          { label: "Rev", value: rowData.rev },
          {
            label: "Controlling Document",
            value: rowData.controllingDocument,
          },
          //   { label: "Res. Party/Loc", value: rowData.resPartyLoc },
          { label: "Acceptance Criteria", value: rowData.acceptanceCriteria },
          { label: "Recording Document", value: rowData.recordingDocument },
          { label: "Start Date", value: rowData.startDate },
          { label: "End Date", value: rowData.endDate },
          { label: "Progress", value: rowData.progress },
          { label: "Actual Hrs", value: rowData.actualHrs },
        ]}
      />
    ),
  };
}

function FlagRadioCell({ rowData }) {
  const { mutate, isPending } = useUpdateItp();

  const getInitialFlag = () => {
    if (rowData.flag) return rowData.flag;
    if (rowData.flagGreen) return "green";
    if (rowData.flagYellow) return "yellow";
    if (rowData.flagRed) return "red";
    return "";
  };

  const [selectedFlag, setSelectedFlag] = useState(getInitialFlag());

  useEffect(() => {
    setSelectedFlag(getInitialFlag());
  }, [rowData.flag, rowData.flagGreen, rowData.flagYellow, rowData.flagRed]);

  const handleChange = (event) => {
    event.stopPropagation();

    const nextFlag = event.target.value;
    setSelectedFlag(nextFlag);
    rowData.flag = nextFlag;

    if (!rowData?.rawItp?.id) return;

    mutate({
      values: {
        ...rowData.rawItp,
        flag: nextFlag,
      },
    });
  };

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <RadioGroup
        row
        value={selectedFlag}
        onChange={handleChange}
        sx={{ flexWrap: "nowrap", gap: 0.5 }}
      >
        <Radio
          value="green"
          disabled={isPending}
          sx={{
            color: "green",
            "&.Mui-checked": { color: "green" },
            p: 0.25,
          }}
        />
        <Radio
          value="yellow"
          disabled={isPending}
          sx={{
            color: "#e0b000",
            "&.Mui-checked": { color: "#e0b000" },
            p: 0.25,
          }}
        />
        <Radio
          value="red"
          disabled={isPending}
          sx={{
            color: "red",
            "&.Mui-checked": { color: "red" },
            p: 0.25,
          }}
        />
      </RadioGroup>
    </Box>
  );
}

function ConclusionDialogContent({ rowData, closeDialog }) {
  const { mutate, isPending } = useUpdateItp();

  const [conclusionValue, setConclusionValue] = useState(
    rowData?.conclusion || "",
  );

  useEffect(() => {
    setConclusionValue(rowData?.conclusion || "");
  }, [rowData?.conclusion]);

  const handleSave = (event) => {
    event.stopPropagation();

    const nextConclusion = conclusionValue || "";

    rowData.conclusion = nextConclusion;

    if (rowData?.rawItp) {
      rowData.rawItp.conclusion = nextConclusion;
    }

    if (!rowData?.rawItp?.id) {
      closeDialog();
      return;
    }

    mutate({
      values: {
        ...rowData.rawItp,
        conclusion: nextConclusion,
      },
    });

    closeDialog();
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 1,
      }}
    >
      <TextField
        label="Conclusion"
        value={conclusionValue}
        onChange={(event) => setConclusionValue(event.target.value)}
        fullWidth
        multiline
        minRows={5}
        disabled={isPending}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={(event) => {
            event.stopPropagation();
            closeDialog();
          }}
          disabled={isPending}
        >
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSave} disabled={isPending}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

const MAINTENANCE_FORM_COLUMNS = [
  {
    key: "vendorPartNo",
    label: "Vendor Part #",
    width: 150,
  },
  {
    key: "partDescription",
    label: "Part Description",
    width: 220,
  },
  {
    key: "cost",
    label: "Cost",
    width: 110,
  },
  {
    key: "deliverySchedule",
    label: "Delivery Schedule",
    width: 170,
  },
  {
    key: "shelfLife",
    label: "Shelf Life",
    width: 130,
  },
  {
    key: "status",
    label: "Status",
    width: 120,
  },
  {
    key: "qty",
    label: "Qty",
    width: 90,
  },
  {
    key: "remarks",
    label: "Remarks",
    width: 240,
  },
];

function createEmptyMaintenanceFormRow() {
  return {
    id: `maintenance-form-${Date.now()}-${Math.random()}`,
    vendorPartNo: "",
    partDescription: "",
    cost: "",
    deliverySchedule: "",
    shelfLife: "",
    status: "",
    qty: "",
    remarks: "",
    isNew: true,
    isEditing: true,
  };
}

function normalizeMaintenanceFormRows(value) {
  if (Array.isArray(value)) {
    return value.map((item, index) => ({
      id: item?.id || `maintenance-form-existing-${index}`,
      vendorPartNo: item?.vendorPartNo || item?.["Vendor Part #"] || "",
      partDescription:
        item?.partDescription || item?.["Part Description"] || "",
      cost: item?.cost || item?.Cost || "",
      deliverySchedule:
        item?.deliverySchedule || item?.["Delivery Schedule"] || "",
      shelfLife: item?.shelfLife || item?.["Shelf Life"] || "",
      status: item?.status || item?.Status || "",
      qty: item?.qty || item?.Qty || "",
      remarks: item?.remarks || item?.Remarks || "",
      isNew: false,
      isEditing: false,
    }));
  }

  if (value && typeof value === "object") {
    return [
      {
        id: value?.id || "maintenance-form-existing-0",
        vendorPartNo: value?.vendorPartNo || value?.["Vendor Part #"] || "",
        partDescription:
          value?.partDescription || value?.["Part Description"] || "",
        cost: value?.cost || value?.Cost || "",
        deliverySchedule:
          value?.deliverySchedule || value?.["Delivery Schedule"] || "",
        shelfLife: value?.shelfLife || value?.["Shelf Life"] || "",
        status: value?.status || value?.Status || "",
        qty: value?.qty || value?.Qty || "",
        remarks: value?.remarks || value?.Remarks || "",
        isNew: false,
        isEditing: false,
      },
    ];
  }

  return [];
}

function cleanMaintenanceFormRows(rows) {
  return rows.map(({ isNew, isEditing, ...row }) => row);
}

function MaintenanceFormDialogContent({ rowData, closeDialog }) {
  const [rows, setRows] = useState(() => {
    const normalizedRows = normalizeMaintenanceFormRows(
      rowData?.maintenanceForm,
    );

    return normalizedRows.length > 0
      ? normalizedRows
      : [createEmptyMaintenanceFormRow()];
  });

  useEffect(() => {
    const normalizedRows = normalizeMaintenanceFormRows(
      rowData?.maintenanceForm,
    );

    setRows(
      normalizedRows.length > 0
        ? normalizedRows
        : [createEmptyMaintenanceFormRow()],
    );
  }, [rowData?.maintenanceForm]);

  const handleCellChange = (rowId, fieldName) => (event) => {
    const value = event.target.value;

    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [fieldName]: value,
            }
          : row,
      ),
    );
  };

  const handleAddRow = (event) => {
    event.stopPropagation();

    setRows((prev) => [...prev, createEmptyMaintenanceFormRow()]);
  };

  const handleSaveRow = (event, rowId) => {
    event.stopPropagation();

    setRows((prev) => {
      const updatedRows = prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              isNew: false,
              isEditing: false,
            }
          : row,
      );

      rowData.maintenanceForm = cleanMaintenanceFormRows(updatedRows);

      return updatedRows;
    });
  };

  const handleEditRow = (event, rowId) => {
    event.stopPropagation();

    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              isEditing: true,
            }
          : row,
      ),
    );
  };

  const handleDeleteRow = (event, rowId) => {
    event.stopPropagation();

    setRows((prev) => {
      const updatedRows = prev.filter((row) => row.id !== rowId);

      rowData.maintenanceForm = cleanMaintenanceFormRows(updatedRows);

      return updatedRows.length > 0
        ? updatedRows
        : [createEmptyMaintenanceFormRow()];
    });
  };

  const handleClose = (event) => {
    event.stopPropagation();
    rowData.maintenanceForm = cleanMaintenanceFormRows(rows);
    closeDialog();
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddRow}
        >
          Add Row
        </Button>
      </Box>

      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: 1280,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {MAINTENANCE_FORM_COLUMNS.map((column) => (
                <Box
                  component="th"
                  key={column.key}
                  sx={{
                    width: column.width,
                    borderBottom: "1px solid #d0d0d0",
                    borderRight: "1px solid #e0e0e0",

                    p: 1,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {column.label}
                </Box>
              ))}

              <Box
                component="th"
                sx={{
                  width: 120,
                  borderBottom: "1px solid #d0d0d0",

                  p: 1,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Actions
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {rows.map((row) => {
              const isEditable = row.isNew || row.isEditing;

              return (
                <Box component="tr" key={row.id}>
                  {MAINTENANCE_FORM_COLUMNS.map((column) => (
                    <Box
                      component="td"
                      key={column.key}
                      sx={{
                        borderBottom: "1px solid #eeeeee",
                        borderRight: "1px solid #eeeeee",
                        p: 0.75,
                        verticalAlign: "top",
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={row[column.key] || ""}
                        onChange={handleCellChange(row.id, column.key)}
                        variant="outlined"
                        InputProps={{
                          readOnly: !isEditable,
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            color: "text.primary",
                            WebkitTextFillColor: "inherit",
                            fontWeight: isEditable ? 400 : 600,
                          },
                          "& .MuiOutlinedInput-root": {
                            // backgroundColor:isEditable ? "white" : "#fafafa",
                            border: isEditable
                              ? "1px solid #d0d0d0"
                              : "text.primary",
                          },
                        }}
                      />
                    </Box>
                  ))}

                  <Box
                    component="td"
                    sx={{
                      borderBottom: "1px solid #eeeeee",
                      p: 0.75,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isEditable ? (
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(event) => handleSaveRow(event, row.id)}
                        >
                          <SaveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(event) => handleEditRow(event, row.id)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(event) => handleDeleteRow(event, row.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
}

function buildMaintenanceFormDialog(rowData) {
  return {
    title: `Maintenance form — ${rowData.tagNo || rowData.tag || "row"}`,
    maxWidth: "xl",
    renderContent: ({ closeDialog }) => (
      <MaintenanceFormDialogContent
        rowData={rowData}
        closeDialog={closeDialog}
      />
    ),
  };
}

function ConclusionButtonCell({ rowData }) {
  const { openDialog } = useDynamicDialog();

  const hasConclusion = String(rowData?.conclusion || "").trim().length > 0;

  const handleOpen = (event) => {
    event.stopPropagation();

    openDialog({
      title: hasConclusion ? "Review Conclusion" : "Add Conclusion",
      maxWidth: "sm",
      renderContent: ({ closeDialog }) => (
        <ConclusionDialogContent rowData={rowData} closeDialog={closeDialog} />
      ),
    });
  };

  return (
    <Button
      variant={hasConclusion ? "outlined" : "contained"}
      size="small"
      onClick={handleOpen}
      sx={{
        whiteSpace: "nowrap",
        textTransform: "none",
      }}
    >
      {hasConclusion ? "Review Conclusion" : "Add Conclusion"}
    </Button>
  );
}

function buildInspectionFindingColumns() {
  return [
    {
      accessorKey: "actions",
      header: "Actions",
      size: 100,
      enableSorting: false,
      Cell: () => <Chip label="Open" size="small" color="primary" />,
    },
    {
      accessorKey: "no",
      header: "Inspection ID",
      size: 100,
    },
    {
      accessorKey: "inspectionTestActivity",
      header: "INSPECTION/TEST ACTIVITY",
      size: 240,
    },
    {
      accessorKey: "rev",
      header: "rev",
      size: 80,
    },
    {
      accessorKey: "controllingDocument",
      header: "CONTROLLING DOCUMENT",
      size: 220,
    },
    {
      accessorKey: "acceptanceCriteria",
      header: "ACCEPTANCE CRITERIA",
      size: 220,
    },
    {
      accessorKey: "recordingDocument",
      header: "RECORDING DOCUMENT",
      size: 220,
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      size: 120,
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      size: 120,
    },
    {
      accessorKey: "progress",
      header: "Progress",
      size: 120,
    },
    {
      accessorKey: "actualHrs",
      header: "Actual Hrs",
      size: 120,
    },
    createIconPopupActionColumn({
      id: "findingChecklistAction",
      header: "",
      tooltip: "Checklist",
      icon: <TaskAltIcon fontSize="small" />,
      color: "primary",
      dialogBuilder: (rowData) => buildFindingSideDialog(rowData, "Checklist"),
      size: 64,
    }),
    createIconPopupActionColumn({
      id: "findingCommentsAction",
      header: "",
      tooltip: "Comments",
      icon: <CommentIcon fontSize="small" />,
      color: "secondary",
      dialogBuilder: (rowData) => buildFindingSideDialog(rowData, "Comments"),
      size: 64,
    }),
    createIconPopupActionColumn({
      id: "findingAttachmentsAction",
      header: "",
      tooltip: "Attachments",
      icon: <UploadFileIcon fontSize="small" />,
      color: "success",
      dialogBuilder: (rowData) =>
        buildFindingSideDialog(rowData, "Attachments"),
      size: 64,
    }),
    {
      header: "INSPECTED",
      id: "authorizedInspectorGroup",
      columns: [
        {
          accessorKey: "authorizedInspectorType",
          header: "Type",
          size: 120,
        },
        {
          accessorKey: "authorizedInspectorSign",
          header: "Sign",
          size: 120,
        },
        {
          accessorKey: "authorizedInspectorDate",
          header: "Date",
          size: 120,
        },
      ],
    },
    {
      header: "CHECKER",
      id: "shopGroup",
      columns: [
        {
          accessorKey: "shopType",
          header: "Type",
          size: 120,
        },
        {
          accessorKey: "shopSign",
          header: "Sign",
          size: 120,
        },
        {
          accessorKey: "shopDate",
          header: "Date",
          size: 120,
        },
      ],
    },
    {
      header: "APPROVER",
      id: "clientGroup",
      columns: [
        {
          accessorKey: "clientType",
          header: "Type",
          size: 120,
        },
        {
          accessorKey: "clientSign",
          header: "Sign",
          size: 120,
        },
        {
          accessorKey: "clientDate",
          header: "Date",
          size: 120,
        },
      ],
    },
    {
      accessorKey: "conclusion",
      header: "Conclusion",
      size: 180,
      enableSorting: false,
      Cell: ({ row }) => <ConclusionButtonCell rowData={row.original} />,
    },
    {
      id: "flag",
      header: "Flag",
      size: 140,
      enableSorting: false,
      Cell: ({ row }) => <FlagRadioCell rowData={row.original} />,
    },
  ];
}

function getInspectionFindingRowsFromRegistryRow(rowData) {
  if (Array.isArray(rowData.inspectionFindingRows)) {
    return rowData.inspectionFindingRows;
  }

  return (rowData.inspectionPlan || []).flatMap(
    (planRow) => planRow.inspectionFindingDetails || [],
  );
}

function buildInspectionFindingDialog(rowData) {
  const isRegistryRow = Array.isArray(rowData.inspectionPlan);

  const findingRows = isRegistryRow
    ? getInspectionFindingRowsFromRegistryRow(rowData)
    : rowData.inspectionFindingDetails || [];

  const dialogTitle = isRegistryRow
    ? `Inspection Plan - ${rowData?.tagNo}`
    : `Inspection Plan`;

  return {
    title: dialogTitle,
    maxWidth: "xl",
    renderContent: () => (
      <DialogTableContent
        columns={buildInspectionFindingColumns()}
        data={findingRows}
        tableProps={{
          enableTopToolbar: false,
          initialState: {
            pagination: { pageIndex: 0, pageSize: 5 },
          },
        }}
      />
    ),
  };
}

/* ------------------------------------------
   INSPECTION PLAN
------------------------------------------ */

function buildInspectionPlanColumns() {
  return [
    {
      accessorKey: "date",
      header: "Date",
      size: 140,
    },
    {
      accessorKey: "inspectionBy",
      header: "Inspection by",
      size: 180,
    },
    createPopupActionColumn({
      id: "inspectionFindingAction",
      header: "Inspection finding",
      buttonLabel: "Open",
      dialogBuilder: buildInspectionFindingDialog,
      buttonProps: { color: "secondary" },
      size: 180,
    }),
    {
      accessorKey: "flags",
      header: "Flags",
      size: 140,
    },
  ];
}

/* ------------------------------------------
   WORK ORDERS
------------------------------------------ */

function getEmptyWorkOrderForm() {
  return {
    plant: "",
    workOrderNumber: "",
    dateTimeAssigned: "",
    dueDateTime: "",
    estimationHours: "",
    dateTimeCompleted: "",
    laborHours: "",
    type: "",
    priority: "",
    status: "",
    equipment: "",
    workAreaLocation: "",
    line: "",
    task: "",
    projectTaskType: "",
    technician: "",
    requestor: "",
    comments: "",
    costCenter: "",
    account: "",
  };
}

function formatDateToDDMMYYYY(value) {
  if (!value) return "-";

  const normalized = String(value).trim();
  if (!normalized) return "-";

  const dateOnly = normalized.includes("T")
    ? normalized.split("T")[0]
    : normalized;

  const parts = dateOnly.split("-");
  if (parts.length === 3) {
    const [yyyy, mm, dd] = parts;
    if (yyyy && mm && dd) {
      return `${dd}/${mm}/${yyyy}`;
    }
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;

  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}

function buildRedFlagWorkOrderRows(rowData) {
  const redInspectionRows = Array.isArray(rowData?.inspectionFindingRows)
    ? rowData.inspectionFindingRows.filter(
        (item) => String(item?.flag || "").toLowerCase() === "red",
      )
    : [];

  return redInspectionRows.map((item, index) => ({
    id: `red-flag-wo-${item.id || index}`,
    plant: "",
    workOrderNumber: "",
    dateTimeAssigned: item.startDate || "",
    dueDateTime: item.endDate || "",
    estimationHours: "",
    dateTimeCompleted: "",
    laborHours: item.actualHrs || "",
    type: item.rawItp?.type || "",
    priority: "High",
    status: "Red Flag",
    equipment: rowData?.tagNo || "",
    workAreaLocation: item.rawItp?.resPartyLoc || "",
    line: "",
    task: item.inspectionTestActivity || "",
    projectTaskType: "",
    technician: "",
    requestor: "",
    comments:
      item.conclusion ||
      item.acceptanceCriteria ||
      item.controllingDocument ||
      "",
    costCenter: "",
    account: "",
    source: "redFlagItp",
  }));
}

function buildWorkOrdersColumns() {
  return [
    {
      accessorKey: "plant",
      header: "Plant",
      size: 140,
    },
    {
      accessorKey: "workOrderNumber",
      header: "Work Order Number",
      size: 180,
    },
    // {
    //   accessorKey: "dateTimeAssigned",
    //   header: "Date/Time Assigned",
    //   size: 170,
    // },
    // {
    //   accessorKey: "dueDateTime",
    //   header: "Due/Date Time",
    //   size: 160,
    // },
    {
      accessorKey: "dateTimeAssigned",
      header: "Date/Time Assigned",
      size: 170,
      Cell: ({ row }) => formatDateToDDMMYYYY(row.original.dateTimeAssigned),
    },
    {
      accessorKey: "dueDateTime",
      header: "Due/Date Time",
      size: 160,
      Cell: ({ row }) => formatDateToDDMMYYYY(row.original.dueDateTime),
    },

    {
      accessorKey: "estimationHours",
      header: "Estimation Hours",
      size: 150,
    },
    {
      accessorKey: "dateTimeCompleted",
      header: "Date/Time Completed",
      size: 180,
      Cell: ({ row }) => formatDateToDDMMYYYY(row.original.dateTimeCompleted),
    },
    // {
    //   accessorKey: "dateTimeCompleted",
    //   header: "Date/Time Completed",
    //   size: 180,
    // },
    {
      accessorKey: "laborHours",
      header: "Labor Hours",
      size: 130,
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 120,
    },
    {
      accessorKey: "priority",
      header: "Priority",
      size: 120,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
    },
    {
      accessorKey: "equipment",
      header: "Equipment",
      size: 160,
    },
    {
      accessorKey: "workAreaLocation",
      header: "Work/Area Location",
      size: 180,
    },
    {
      accessorKey: "line",
      header: "Line",
      size: 120,
    },
    {
      accessorKey: "task",
      header: "Task",
      size: 180,
    },
    {
      accessorKey: "projectTaskType",
      header: "Project Type",
      size: 170,
      Cell: ({ row }) => {
        const value = row.original.projectTaskType;
        if (value === "modificationProject") return "Modification Project";
        if (value === "maintenanceProject") return "Maintenance Project";
        return value || "-";
      },
    },
    {
      accessorKey: "technician",
      header: "Technician",
      size: 160,
    },
    {
      accessorKey: "requestor",
      header: "Requestor",
      size: 160,
    },
    {
      accessorKey: "comments",
      header: "Comments",
      size: 220,
    },
    {
      accessorKey: "costCenter",
      header: "Cost Center",
      size: 150,
    },
    {
      accessorKey: "account",
      header: "Account",
      size: 150,
    },
  ];
}

function parseTaskCode(taskCode) {
  if (!taskCode || typeof taskCode !== "string") {
    return { code: "", groupName: "" };
  }

  const value = String(taskCode).trim();
  const dashIndex = value.indexOf("-");

  if (dashIndex < 0) {
    return { code: value, groupName: "" };
  }

  return {
    code: value.slice(0, dashIndex).trim(),
    groupName: value.slice(dashIndex + 1).trim(),
  };
}

function getCodePrefix(code) {
  if (!code || typeof code !== "string") return null;

  const parts = String(code).trim().split(".").filter(Boolean);

  if (parts.length < 2) {
    return parts[0] || null;
  }

  return parts.slice(0, 2).join(".");
}

function startsWithExistingPrefix(code, existingPrefixes) {
  if (!code || !Array.isArray(existingPrefixes)) return false;

  const normalized = String(code).trim();

  return existingPrefixes.some(
    (prefix) =>
      prefix && (normalized === prefix || normalized.startsWith(`${prefix}.`)),
  );
}

function normalizeDateForInput(value) {
  if (!value) return "";

  const normalized = String(value).trim();

  if (!normalized) return "";

  return normalized.includes("T") ? normalized.slice(0, 10) : normalized;
}

function normalize4DTasksResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.tasks)) {
    return response.tasks;
  }

  if (Array.isArray(response?.data?.tasks)) {
    return response.data.tasks;
  }

  return [];
}

function getStoredAccessToken() {
  const directToken =
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("authToken");

  if (directToken) return directToken;

  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    return (
      storedUser?.accessToken ||
      storedUser?.token ||
      storedUser?.jwt ||
      storedUser?.authToken ||
      ""
    );
  } catch {
    return "";
  }
}

function WorkOrdersDialogContent({ rowData }) {
  const params = useParams();
  const authContextValue = useContext(AuthContext) || {};
  const user = authContextValue.user;

  const effectiveAccessToken =
    authStore.getToken() ||
    authContextValue.accessToken ||
    authContextValue.token ||
    getStoredAccessToken();
  const { plantId: amsPlantId } = useAmsModuleData();

  const routeId = params?.id || "";
  const routePlantId = params?.plantId || "";
  const routeProjectId = params?.projectId || "";

  const resolvedPlantId =
    amsPlantId || routePlantId || rowData?.plantId || routeId || "";

  const resolvedProjectId = routeProjectId || rowData?.pmsProjectId || "";

  const plantMode = Boolean(resolvedPlantId);

  const [formValues, setFormValues] = useState(getEmptyWorkOrderForm());

  const [tableRows, setTableRows] = useState(() =>
    buildRedFlagWorkOrderRows(rowData),
  );

  const [rows, setRows] = useState([]);
  const [, setAllTasks] = useState([]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);

  const [newTask, setNewTask] = useState({
    task: "",
    taskCode: "",
    type: "engineering",
    startDate: "",
    endDate: "",
    estimatedTime: 0,
  });

  const [wbsMode, setWbsMode] = useState("existing");
  const [wbsSuffix, setWbsSuffix] = useState("");
  const [wbsFull, setWbsFull] = useState("");
  const [groupName, setGroupName] = useState("");
  const [wbsModeTouched, setWbsModeTouched] = useState(false);
  const [loading4DTasks, setLoading4DTasks] = useState(false);

  const prevDialogOpenRef = useRef(false);

  useEffect(() => {
    const initialRows = buildRedFlagWorkOrderRows(rowData);
    setTableRows(initialRows);
    rowData.workOrders = initialRows;
  }, [rowData]);

  const groupNameToPrefix = useMemo(() => {
    const taskCodes = (rows || []).map((row) => row.taskCode).filter(Boolean);
    const map = new Map();

    taskCodes.forEach((taskCode) => {
      const { code, groupName: parsedGroupName } = parseTaskCode(taskCode);

      if (parsedGroupName && code) {
        const prefix = getCodePrefix(code);

        if (prefix && !map.has(parsedGroupName)) {
          map.set(parsedGroupName, prefix);
        }
      }
    });

    return map;
  }, [rows]);

  const existingGroupNames = useMemo(() => {
    const taskCodes = (rows || []).map((row) => row.taskCode).filter(Boolean);
    const names = new Set();

    taskCodes.forEach((taskCode) => {
      const { groupName: parsedGroupName } = parseTaskCode(taskCode);

      if (parsedGroupName) {
        names.add(parsedGroupName);
      }
    });

    return Array.from(names).sort();
  }, [rows]);

  const autoCodePrefix = groupName
    ? groupNameToPrefix.get(groupName) || ""
    : "";

  const existingCodePrefixes = useMemo(
    () => Array.from(groupNameToPrefix.values()),
    [groupNameToPrefix],
  );

  const computedCode = useMemo(() => {
    if (wbsMode === "existing") {
      const suffix = String(wbsSuffix || "")
        .trim()
        .replace(/^\.+/, "");

      const prefix = autoCodePrefix;

      if (!prefix || !suffix) return "";

      return `${prefix}.${suffix}`;
    }

    return String(wbsFull || "").trim();
  }, [wbsMode, autoCodePrefix, wbsSuffix, wbsFull]);

  const computedTaskCode = useMemo(() => {
    const code = computedCode.trim();
    const normalizedGroupName = String(groupName || "").trim();

    if (!code || !normalizedGroupName) return "";

    return `${code}-${normalizedGroupName}`;
  }, [computedCode, groupName]);

  const newGroupError = useMemo(() => {
    if (wbsMode !== "new" || !wbsFull) return null;

    const code = String(wbsFull).trim();

    if (!code) return null;

    const segments = code.split(".").filter(Boolean);

    if (segments.length !== 2) {
      return "Group code must have exactly 2 segments (e.g. 2.8).";
    }

    if (startsWithExistingPrefix(code, existingCodePrefixes)) {
      return "This code starts with an existing group. Use 'Use existing group' instead.";
    }

    return null;
  }, [wbsMode, wbsFull, existingCodePrefixes]);

  const formatTasksFor4D = useCallback((tasks) => {
    if (!Array.isArray(tasks)) return [];

    const normalizeDate = (value) => {
      if (!value) return null;
      return value;
    };

    return tasks
      .filter((taskItem) => !taskItem.isArchieved)
      .map((taskItem) => {
        const rawProgress = taskItem.progress ?? 0;
        const progress = Number.isNaN(Number(rawProgress))
          ? 0
          : Number(rawProgress);

        const estimatedTime =
          taskItem.estimatedTime != null
            ? Number(taskItem.estimatedTime)
            : taskItem.manhours != null
              ? Number(taskItem.manhours)
              : 0;

        const hours = taskItem.hours != null ? Number(taskItem.hours) : null;

        return {
          id: taskItem.id,
          taskCode: taskItem.taskCode || "",
          name: taskItem.name || null,
          task: taskItem.task || null,
          type: (taskItem.type || "").toLowerCase(),
          progress,
          startDate: normalizeDate(taskItem.startDate),
          endDate: normalizeDate(taskItem.endDate || taskItem.targetDate),
          revisedStart: normalizeDate(
            taskItem.revisedStart || taskItem.revisedStartDate,
          ),
          revisedEnd: normalizeDate(
            taskItem.revisedEnd || taskItem.revisedEndDate,
          ),
          actualStart: normalizeDate(taskItem.actualStart),
          actualEnd: normalizeDate(taskItem.actualEnd),
          estimatedTime,
          manhours:
            taskItem.manhours != null ? Number(taskItem.manhours) : null,
          hours,
          mrqMaterials: taskItem.mrqMaterials,
          mrvMaterials: taskItem.mrvMaterials,
        };
      })
      .filter((task) => task.taskCode);
  }, []);

  const buildRowsFromTasks = useCallback(
    (tasks) => {
      if (!Array.isArray(tasks) || tasks.length === 0) {
        setRows([]);
        setAllTasks([]);
        return;
      }

      const formattedTasks = formatTasksFor4D(tasks);

      if (formattedTasks.length === 0) {
        setRows([]);
        setAllTasks([]);
        return;
      }

      setAllTasks(formattedTasks);

      const toDateStr = (value) => {
        if (!value) return "";
        return typeof value === "string" ? value.slice(0, 10) : "";
      };

      const rowsForTable = formattedTasks.map((task) => {
        const type = (task.type || "").toLowerCase();
        const progressValue = Number.isNaN(Number(task.progress))
          ? 0
          : Number(task.progress);

        let fourD = "";
        if (type === "engineering") fourD = "DCS";
        else if (type === "procurement") fourD = "MCS";
        else if (type === "fabrication") fourD = "PCS";
        else if (type === "itp") fourD = "ITP";

        const { code: displayCode } = parseTaskCode(task.taskCode || "");

        return {
          id: task.id,
          task: task.task || task.name || "",
          type: task.type || null,
          taskCode: task.taskCode,
          fourD,
          wbsId: displayCode || task.taskCode || "",
          mrqNumber: "",
          progress: progressValue.toFixed(0),
          plannedStart: toDateStr(task.startDate),
          plannedEnd: toDateStr(task.endDate),
          revisedStart: toDateStr(task.revisedStart),
          revisedEnd: toDateStr(task.revisedEnd),
          actualStart: toDateStr(task.actualStart),
          actualEnd: toDateStr(task.actualEnd),
          budgetedHrDollar: task.estimatedTime || 0,
          actualHrDollar: task.manhours ?? 0,
        };
      });

      const sortedRows = rowsForTable.sort((a, b) => {
        const dateA = a.plannedStart ? new Date(a.plannedStart) : new Date(0);
        const dateB = b.plannedStart ? new Date(b.plannedStart) : new Date(0);
        return dateA - dateB;
      });

      setRows(sortedRows);
    },
    [formatTasksFor4D],
  );

  const arrange4dData = useCallback(async () => {
    console.log("[AMS arrange4dData] CALLED", {
      hasAccessToken: Boolean(effectiveAccessToken),
      plantMode,
      resolvedPlantId,
      resolvedProjectId,
    });

    if (!effectiveAccessToken) {
      console.warn("[AMS arrange4dData] STOPPED: missing accessToken");
      setRows([]);
      setAllTasks([]);
      return [];
    }

    try {
      let response;
      let tasksArray = [];

      if (plantMode) {
        if (!resolvedPlantId) {
          console.warn("[AMS arrange4dData] STOPPED: missing resolvedPlantId");
          setRows([]);
          setAllTasks([]);
          return [];
        }

        console.log("[AMS arrange4dData] calling getPlantData", {
          resolvedPlantId,
        });

        response = await getPlantData(resolvedPlantId, effectiveAccessToken);

        console.log("[AMS arrange4dData] raw getPlantData response", response);

        tasksArray = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : [];
      } else {
        if (!resolvedProjectId) {
          console.warn(
            "[AMS arrange4dData] STOPPED: missing resolvedProjectId",
          );
          setRows([]);
          setAllTasks([]);
          return [];
        }

        console.log("[AMS arrange4dData] calling getProjectData", {
          resolvedProjectId,
        });

        response = await getProjectData(
          resolvedProjectId,
          effectiveAccessToken,
        );

        console.log(
          "[AMS arrange4dData] raw getProjectData response",
          response,
        );

        tasksArray = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : [];
      }

      console.log("[AMS arrange4dData] normalized tasksArray", tasksArray);

      buildRowsFromTasks(tasksArray);

      return tasksArray;
    } catch (error) {
      console.error("[AMS arrange4dData] ERROR", error);
      setRows([]);
      setAllTasks([]);
      return [];
    }
  }, [
    effectiveAccessToken,
    plantMode,
    resolvedPlantId,
    resolvedProjectId,
    buildRowsFromTasks,
  ]);

  useEffect(() => {
    arrange4dData();
  }, [arrange4dData]);

  useEffect(() => {
    const justOpened = createDialogOpen && !prevDialogOpenRef.current;
    prevDialogOpenRef.current = createDialogOpen;

    if (justOpened) {
      setWbsModeTouched(false);
      setWbsMode(existingGroupNames.length > 0 ? "existing" : "new");
      setWbsSuffix("");
      setWbsFull("");
      setGroupName("");
    }
  }, [createDialogOpen, existingGroupNames.length]);

  useEffect(() => {
    if (!createDialogOpen) return;
    if (wbsModeTouched) return;

    if (existingGroupNames.length > 0) {
      setWbsMode("existing");
    } else {
      setWbsMode("new");
    }
  }, [createDialogOpen, existingGroupNames.length, wbsModeTouched]);

  const handleFieldChange = (fieldName) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: event.target.value,
    }));
  };

  const handleNewTaskChange = (fieldName) => (event) => {
    const value = event.target.value;

    setNewTask((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleProjectTypeChange = async (event) => {
    const selectedType = event.target.value;

    const nextFormValues = {
      ...formValues,
      projectTaskType: selectedType,
    };

    setFormValues(nextFormValues);

    setNewTask({
      task: nextFormValues.task || "",
      taskCode: "",
      type: "engineering",
      startDate: normalizeDateForInput(nextFormValues.dateTimeAssigned),
      endDate: normalizeDateForInput(nextFormValues.dueDateTime),
      estimatedTime: nextFormValues.estimationHours || 0,
    });

    setWbsSuffix("");
    setWbsFull("");
    setGroupName("");

    const tasksArray = await arrange4dData();

    const hasExistingGroups = Array.isArray(tasksArray)
      ? tasksArray.some((taskItem) => {
          const { groupName: parsedGroupName } = parseTaskCode(
            taskItem?.taskCode || "",
          );

          return Boolean(parsedGroupName);
        })
      : false;

    console.log("[AMS handleProjectTypeChange] hasExistingGroups", {
      hasExistingGroups,
      tasksLength: tasksArray.length,
    });

    setWbsMode(hasExistingGroups ? "existing" : "new");
    setCreateDialogOpen(true);
  };

  const handleCreateTask = async () => {
    const taskCode = computedTaskCode;
    const taskName = wbsMode === "new" ? groupName.trim() : newTask.task;

    if (!taskName || !taskCode || newGroupError) return;

    if (!effectiveAccessToken) {
      console.warn("[AMS handleCreateTask] missing access token");
      return;
    }
    if (plantMode && !resolvedPlantId) {
      console.warn("[AMS handleCreateTask] plant mode but no plant id");
      return;
    }
    if (!plantMode && !resolvedProjectId) {
      console.warn("[AMS handleCreateTask] project mode but no project id");
      return;
    }

    try {
      setCreatingTask(true);
      const loggedInUserName = user?.name ?? user?.email ?? "";
      const payload = {
        task: taskName,
        taskCode,
        type: newTask.type,
        startDate: newTask.startDate || null,
        endDate: newTask.endDate || null,
        estimatedTime: newTask.estimatedTime || 0,
        status: "Not Started",
        priority: "Medium",
        task_members: loggedInUserName || "",
      };

      await create4DTask({
        task: payload,
        projectId: plantMode ? null : resolvedProjectId,
        plantId: plantMode ? resolvedPlantId : null,
        accessToken: effectiveAccessToken,
      });

      const wasNewGroup = wbsMode === "new";
      const createdGroupName = groupName.trim();

      const estimationValue =
        newTask.estimatedTime !== undefined &&
        newTask.estimatedTime !== null &&
        String(newTask.estimatedTime) !== ""
          ? String(newTask.estimatedTime)
          : "";

      setFormValues((prev) => ({
        ...prev,
        task: taskName || prev.task,
        dateTimeAssigned: newTask.startDate || prev.dateTimeAssigned,
        dueDateTime: newTask.endDate || prev.dueDateTime,
        estimationHours: estimationValue || prev.estimationHours,
      }));

      setNewTask({
        task: "",
        taskCode: "",
        type: "engineering",
        startDate: "",
        endDate: "",
        estimatedTime: 0,
      });
      setWbsSuffix("");
      setWbsFull("");

      await arrange4dData();

      if (wasNewGroup && createdGroupName) {
        setWbsMode("existing");
        setGroupName(createdGroupName);
      } else {
        setCreateDialogOpen(false);
        setGroupName("");
        setWbsMode("existing");
      }
    } catch (e) {
      console.error("[AMS handleCreateTask] Error creating 4D task:", e);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleAddRow = () => {
    const isCompletelyEmpty = Object.values(formValues).every(
      (value) => String(value || "").trim() === "",
    );

    if (isCompletelyEmpty) return;

    const newRow = {
      id: `wo-row-${Date.now()}`,
      ...formValues,
    };

    setTableRows((prev) => {
      const updatedRows = [...prev, newRow];
      rowData.workOrders = updatedRows;
      return updatedRows;
    });

    setFormValues(getEmptyWorkOrderForm());
  };

  const renderTextField = (field) => (
    <Grid item xs={12} md={field.md ?? 6} key={field.name}>
      <TextField
        fullWidth
        label={field.label}
        name={field.name}
        type={field.type || "text"}
        value={formValues[field.name] ?? ""}
        onChange={handleFieldChange(field.name)}
        multiline={field.multiline}
        minRows={field.minRows}
        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
      />
    </Grid>
  );

  const beforeTaskFields = [
    { name: "plant", label: "Plant" },
    { name: "workOrderNumber", label: "Work Order Number" },
    { name: "dateTimeAssigned", label: "Date/Time Assigned", type: "date" },
    { name: "dueDateTime", label: "Due/Date Time", type: "date" },
    { name: "estimationHours", label: "Estimation Hours" },
    { name: "dateTimeCompleted", label: "Date/Time Completed", type: "date" },
    { name: "laborHours", label: "Labor Hours" },
    { name: "type", label: "Type" },
    { name: "priority", label: "Priority" },
    { name: "status", label: "Status" },
    { name: "equipment", label: "Equipment" },
    { name: "workAreaLocation", label: "Work/Area Location" },
    { name: "line", label: "Line" },
  ];

  const afterTaskFields = [
    { name: "technician", label: "Technician" },
    { name: "requestor", label: "Requestor" },
    {
      name: "comments",
      label: "Comments",
      multiline: true,
      minRows: 4,
      md: 12,
    },
    { name: "costCenter", label: "Cost Center" },
    { name: "account", label: "Account" },
  ];

  return (
    <Box>
      <BaseDataTable
        columns={buildWorkOrdersColumns()}
        data={tableRows}
        mrtProps={{
          enableTopToolbar: false,
          initialState: {
            pagination: { pageIndex: 0, pageSize: 5 },
          },
        }}
      />

      <Box mt={3}>
        <Typography variant="h6" mb={2}>
          Work Order Input Fields
        </Typography>

        <Grid container spacing={2}>
          {beforeTaskFields.map(renderTextField)}

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <TextField
                  fullWidth
                  label="Task"
                  name="task"
                  value={formValues.task}
                  onChange={handleFieldChange("task")}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 320,
                  pt: { xs: 0, md: 1 },
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel component="legend">Project Type</FormLabel>

                  <RadioGroup
                    row
                    value={formValues.projectTaskType}
                    onChange={handleProjectTypeChange}
                  >
                    <FormControlLabel
                      value="modificationProject"
                      control={<Radio />}
                      label="Modification Project"
                    />

                    <FormControlLabel
                      value="maintenanceProject"
                      control={<Radio />}
                      label="Maintenance Project"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
          </Grid>

          {afterTaskFields.map(renderTextField)}

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={handleAddRow}>
                Add
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={createDialogOpen}
        onClose={() => {
          if (!creatingTask) {
            setCreateDialogOpen(false);
            setWbsMode("existing");
            setWbsSuffix("");
            setWbsFull("");
            setGroupName("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>PMS Task Details</DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
          }}
        >
          {wbsMode !== "new" && (
            <TextField
              label="Task Name"
              value={newTask.task}
              onChange={handleNewTaskChange("task")}
              fullWidth
            />
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              WBS ID (code-groupName, e.g. 2.1.1-group1)
            </Typography>

            {loading4DTasks && (
              <Typography variant="body2" color="text.secondary">
                Loading 4D groups...
              </Typography>
            )}

            <RadioGroup
              row
              value={wbsMode}
              onChange={(event) => {
                setWbsModeTouched(true);
                setWbsMode(event.target.value);
                setWbsSuffix("");
                setWbsFull("");
                setGroupName("");
              }}
              sx={{ mb: 1 }}
            >
              {existingGroupNames.length > 0 && (
                <FormControlLabel
                  value="existing"
                  control={<Radio size="small" />}
                  label="Use existing group"
                />
              )}

              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label="New group"
              />
            </RadioGroup>

            {wbsMode === "existing" ? (
              <Stack spacing={1.5}>
                <Autocomplete
                  options={existingGroupNames}
                  value={groupName}
                  onChange={(_, value) => {
                    setGroupName(value || "");
                    setWbsSuffix("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Group (select first)"
                      size="small"
                      placeholder="Select group"
                    />
                  )}
                />

                {groupName && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="Code prefix"
                      value={autoCodePrefix}
                      size="small"
                      sx={{ minWidth: 100 }}
                      InputProps={{ readOnly: true }}
                    />

                    <Typography color="text.secondary">.</Typography>

                    <TextField
                      label="Suffix *"
                      value={wbsSuffix}
                      onChange={(event) => setWbsSuffix(event.target.value)}
                      size="small"
                      placeholder="e.g. 1, 2, 3"
                      required
                    />
                  </Stack>
                )}

                {groupName && wbsSuffix.trim() && (
                  <Typography variant="caption" color="text.secondary">
                    Full WBS:{" "}
                    {computedCode ? `${computedCode}-${groupName}` : "—"}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  label="Full code"
                  value={wbsFull}
                  onChange={(event) => setWbsFull(event.target.value)}
                  fullWidth
                  size="small"
                  error={Boolean(newGroupError)}
                  helperText={newGroupError}
                  placeholder="e.g. 4.1, 5.2"
                />

                <TextField
                  label="Group name"
                  value={groupName}
                  onChange={(event) => setGroupName(event.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g. group1, procurement"
                />

                {groupName && wbsFull && (
                  <Typography variant="caption" color="text.secondary">
                    Full WBS: {wbsFull}-{groupName}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          <TextField
            select
            label="4D"
            value={newTask.type}
            onChange={handleNewTaskChange("type")}
            fullWidth
          >
            <MenuItem value="engineering">DCS (Engineering)</MenuItem>
            <MenuItem value="procurement">MCS (Procurement)</MenuItem>
            <MenuItem value="fabrication">PCS (Fabrication)</MenuItem>
            <MenuItem value="itp">ITP</MenuItem>
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Baseline Start"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newTask.startDate}
              onChange={handleNewTaskChange("startDate")}
              fullWidth
            />

            <TextField
              label="Baseline End"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newTask.endDate}
              onChange={handleNewTaskChange("endDate")}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={creatingTask}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateTask}
            disabled={
              creatingTask ||
              !(wbsMode === "new" ? groupName.trim() : newTask.task) ||
              !computedTaskCode ||
              Boolean(newGroupError)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function buildWorkOrdersDialog(rowData) {
  return {
    title: `Work Orders - ${rowData.tagNo || rowData.tag}`,
    maxWidth: "xl",
    minHeight: 560,
    renderContent: () => <WorkOrdersDialogContent rowData={rowData} />,
  };
}

/* ------------------------------------------
   MAIN REGISTRY TABLE
------------------------------------------ */

export function buildRegistryColumns() {
  return [
    {
      accessorKey: "tagNo",
      header: "Tag#",
      size: 140,
    },
    {
      accessorKey: "components",
      header: "Components",
      size: 180,
    },
    {
      accessorKey: "materialDescription",
      header: "Material Description",
      size: 220,
    },
    createPopupActionColumn({
      id: "sparePartsAction",
      header: "Spare Parts",
      buttonLabel: "Open",
      dialogBuilder: buildSparePartsDialog,
      buttonProps: { color: "primary" },
      size: 160,
    }),
    createPopupActionColumn({
      id: "dataSheetsAction",
      header: "Data Sheets",
      buttonLabel: "Open",
      dialogBuilder: buildDataSheetDialog,
      buttonProps: { color: "info" },
      size: 160,
    }),
    createPopupActionColumn({
      id: "maintenanceFormAction",
      header: "Maintenance form",
      buttonLabel: "Open",
      dialogBuilder: buildMaintenanceFormDialog,
      buttonProps: { color: "warning", variant: "outlined" },
      startIcon: <AssignmentOutlinedIcon fontSize="small" />,
      size: 160,
    }),
    createPopupActionColumn({
      id: "inspectionPlanAction",
      header: "Inspection Plan",
      buttonLabel: "Open",
      dialogBuilder: buildInspectionFindingDialog,
      buttonProps: { color: "secondary" },
      size: 180,
    }),
    createPopupActionColumn({
      id: "workOrdersAction",
      header: "Work Orders",
      buttonLabel: "Open",
      dialogBuilder: buildWorkOrdersDialog,
      buttonProps: { color: "success" },
      size: 160,
    }),
  ];
}

/* ------------------------------------------
   DUMMY DATA
------------------------------------------ */

function createFindingDetailRow(idPrefix, index) {
  return {
    id: `${idPrefix}-finding-${index}`,
    actions: "Open",
    no: index,
    inspectionTestActivity: `Inspection/Test Activity ${index}`,
    rev: `R${index}`,
    controllingDocument: `DOC-${index}`,
    resPartyLoc: `Area-${index}`,
    acceptanceCriteria: `Criteria ${index}`,
    recordingDocument: `REC-${index}`,
    startDate: `2026-04-0${index}`,
    endDate: `2026-04-1${index}`,
    progress: `${index * 20}%`,
    actualHrs: `${index * 2}`,
    conclusion: `Conclusion ${index}`,
    flag:
      index === 1 ? "green" : index === 2 ? "yellow" : index === 3 ? "red" : "",
    authorizedInspectorType: "Authorized",
    authorizedInspectorSign: `AI-SIGN-${index}`,
    authorizedInspectorDate: `2026-04-1${index}`,
    shopType: "Shop",
    shopSign: `SHOP-SIGN-${index}`,
    shopDate: `2026-04-1${index}`,
    clientType: "Client",
    clientSign: `CLIENT-SIGN-${index}`,
    clientDate: `2026-04-1${index}`,
  };
}

function createRegistryRow({
  id,
  tagNo,
  components,
  materialDescription,
  dataSheets,
}) {
  return {
    id,

    // compatibility
    tag: tagNo,
    component: components,

    // main fields
    tagNo,
    components,
    materialDescription,
    dataSheets,
    maintenanceForm: "",

    spareParts: [
      {
        id: `${id}-sp-1`,
        vendorPartNo: "VP-001",
        partDescription: "Mechanical Seal",
        cost: "$120",
        deliverySchedule: "7 Days",
        shelfLife: "12 Months",
        status: "In Stock",
        qty: 2,
        remarks: "Available in store",
      },
      {
        id: `${id}-sp-2`,
        vendorPartNo: "VP-002",
        partDescription: "Bearing Set",
        cost: "$80",
        deliverySchedule: "14 Days",
        shelfLife: "24 Months",
        status: "Available",
        qty: 1,
        remarks: "Need approval before issue",
      },
      {
        id: `${id}-sp-3`,
        vendorPartNo: "VP-003",
        partDescription: "Gasket Kit",
        cost: "$35",
        deliverySchedule: "5 Days",
        shelfLife: "18 Months",
        status: "Pending",
        qty: 4,
        remarks: "Ordered from vendor",
      },
    ],

    inspectionPlan: [
      {
        id: `${id}-ip-1`,
        date: "2026-04-05",
        inspectionBy: "Inspector A",
        flags: "Open",
        inspectionFindingDetails: [
          createFindingDetailRow(`${id}-ip-1`, 1),
          createFindingDetailRow(`${id}-ip-1`, 2),
        ],
      },
      {
        id: `${id}-ip-2`,
        date: "2026-04-12",
        inspectionBy: "Inspector B",
        flags: "Closed",
        inspectionFindingDetails: [
          createFindingDetailRow(`${id}-ip-2`, 1),
          createFindingDetailRow(`${id}-ip-2`, 2),
          createFindingDetailRow(`${id}-ip-2`, 3),
        ],
      },
      {
        id: `${id}-ip-3`,
        date: "2026-04-18",
        inspectionBy: "Inspector C",
        flags: "Pending",
        inspectionFindingDetails: [createFindingDetailRow(`${id}-ip-3`, 1)],
      },
    ],

    workOrders: [],

    // workOrders: [
    //   {
    //     id: `${id}-wo-1`,
    //     workOrderId: "WOID-1001",
    //     workOrderNo: "WO-1001",
    //     dateAssigned: "2026-04-01",
    //     dateDue: "2026-04-05",
    //     dateCompleted: "2026-04-04",
    //     technician: "Tech A",
    //     status: "Closed",
    //     priority: "High",
    //     type: "Preventive",
    //   },
    //   {
    //     id: `${id}-wo-2`,
    //     workOrderId: "WOID-1002",
    //     workOrderNo: "WO-1002",
    //     dateAssigned: "2026-04-08",
    //     dateDue: "2026-04-12",
    //     dateCompleted: "",
    //     technician: "Tech B",
    //     status: "Open",
    //     priority: "Medium",
    //     type: "Corrective",
    //   },
    //   {
    //     id: `${id}-wo-3`,
    //     workOrderId: "WOID-1003",
    //     workOrderNo: "WO-1003",
    //     dateAssigned: "2026-04-15",
    //     dateDue: "2026-04-20",
    //     dateCompleted: "",
    //     technician: "Tech C",
    //     status: "Planned",
    //     priority: "Low",
    //     type: "Inspection",
    //   },
    // ],
  };
}

export const maintenanceRegistryDummyData = {
  equipments: [
    createRegistryRow({
      id: "eq-1",
      tagNo: "EQ-101",
      components: "Centrifugal Pump",
      materialDescription: "Carbon Steel / SS Internals",
      dataSheets: "DS-EQ-101 Rev.0",
    }),
    createRegistryRow({
      id: "eq-2",
      tagNo: "EQ-102",
      components: "Heat Exchanger",
      materialDescription: "Carbon Steel Shell / SS Tubes",
      dataSheets: "DS-EQ-102 Rev.1",
    }),
    createRegistryRow({
      id: "eq-3",
      tagNo: "EQ-103",
      components: "Air Cooler",
      materialDescription: "Galvanized Steel",
      dataSheets: "DS-EQ-103 Rev.0",
    }),
  ],
  valves: [
    createRegistryRow({
      id: "vlv-1",
      tagNo: "XV-201",
      components: "Gate Valve",
      materialDescription: "A216 WCB / SS Trim",
      dataSheets: "DS-VLV-201 Rev.0",
    }),
    createRegistryRow({
      id: "vlv-2",
      tagNo: "FV-202",
      components: "Ball Valve",
      materialDescription: "SS316",
      dataSheets: "DS-VLV-202 Rev.2",
    }),
    createRegistryRow({
      id: "vlv-3",
      tagNo: "PV-203",
      components: "Control Valve",
      materialDescription: "A105 / PTFE Seat",
      dataSheets: "DS-VLV-203 Rev.1",
    }),
  ],
  instruments: [
    createRegistryRow({
      id: "ins-1",
      tagNo: "PT-301",
      components: "Pressure Transmitter",
      materialDescription: "SS316 Wetted Parts",
      dataSheets: "DS-INS-301 Rev.0",
    }),
    createRegistryRow({
      id: "ins-2",
      tagNo: "TT-302",
      components: "Temperature Transmitter",
      materialDescription: "SS316 Probe",
      dataSheets: "DS-INS-302 Rev.1",
    }),
    createRegistryRow({
      id: "ins-3",
      tagNo: "LT-303",
      components: "Level Transmitter",
      materialDescription: "SS316 / PTFE",
      dataSheets: "DS-INS-303 Rev.0",
    }),
  ],
};
